<template>
    <div class="w-100">
        <div
            class="w-100"
            style="max-height: 400px; overflow-y: auto; overflow-x: hidden"
            v-on:scroll="onNotificationsScroll">
            <notification
                v-for="notification in displayedNotifications"
                v-bind:key="notification.PublicId"
                v-bind:notification="notification"
                v-bind:page-load-time-ticks="loadTime"
                v-bind:last-viewed-notification-timestamp-ticks="lastViewedNotificationTimestampTicks" />
            <pc-alert
                v-model:shown="hasFetchError"
                v-bind:severity="alertSeverity"
                v-bind:message="fetchError"
                class="mb-0" />
        </div>
    </div>
</template>
<script>
    import notification from "./notification.vue";
    import { ALERT_SEVERITY } from "Util/alerts";
    import { safeSetItem } from "Util/util";
    const autoFetchDelayMins = 10;

    function isNearBottom(element) {
        return element.clientHeight + element.scrollTop >= element.scrollHeight - 10;
    }

    export default {
        components: {
            notification
        },
        props: {
            loadTime: { type: Number, required: true },
            opened: { type: Boolean, required: true }
        },
        emits: ["has-notifications", "new-notifications"],
        data() {
            return {
                notifications: [],
                fetchingDfd: null,
                hasMore: true,

                fetchError: null,
                alertSeverity: ALERT_SEVERITY.error,

                notificationsCacheKey: null,
                lastFetchedTimestampKey: null,
                lastViewedNotificationTimestampTicks: null
            };
        },
        computed: {
            displayedNotifications() {
                return this.notifications.filter((n) => {
                    return !n.IsHidden;
                });
            },
            hasFetchError() {
                return this.fetchError != null;
            }
        },
        watch: {
            opened() {
                if (this.opened) {
                    this.updateNotificationsSeenTime();
                }
            }
        },
        created() {
            const cacheKeyBase = this.$serviceControllers.NotificationController.NotificationCacheKey;
            this.notificationsCacheKey = `${cacheKeyBase}/Notifications`;
            this.lastFetchedTimestampKey = `${cacheKeyBase}/LastFetchedNotifications`;
            this.lastViewedNotificationTimestampTicks =
                this.$serviceControllers.NotificationController.LastViewedNotificationTimestampTicks;

            this.fetchOrRetrieveNotificationsForStartup();
        },
        methods: {
            async fetchNotifications(isInitialFetch) {
                const self = this;

                // This can be triggered very rapidly so avoid tons of
                // redundant calls
                if (this.fetchingDfd || !this.hasMore) {
                    return;
                }

                try {
                    this.fetchingDfd = true;

                    const beforeTime =
                        this.notifications.length > 0
                            ? this.notifications[this.notifications.length - 1].PublicationTimeUtcTicks
                            : null;

                    this.fetchError = null;
                    const result = await this.$serviceControllers.NotificationController.FetchNotifications({
                        programId: this.$app.ProgramId,
                        beforeTimeUtcTicks: beforeTime
                    });
                    if (result.status === "success") {
                        // Make note of the time of the fetch so we don't auto-fetch unnecessarily
                        // if rapidly changing pages.
                        const now = Date.now();
                        safeSetItem(this.lastFetchedTimestampKey, now);

                        const fetched = result.Data.Notifications;

                        if (isInitialFetch) {
                            const cachedNotifications = this.retrieveCachedNotifications();

                            // If this is our startup fetch and the most recent cached
                            // notification matches the most recent fetched notification,
                            // just use the cached notifications. Otherwise take the fetched
                            // ones
                            if (
                                cachedNotifications.length > 0 &&
                                fetched.length > 0 &&
                                cachedNotifications[0].PublicId == fetched[0].PublicId
                            ) {
                                cachedNotifications.forEach((c) => {
                                    self.notifications.push(c);
                                });
                            } else {
                                fetched.forEach((f) => {
                                    self.notifications.push(f);
                                });
                            }
                        } else {
                            fetched.forEach((f) => {
                                self.notifications.push(f);
                            });
                        }

                        this.cacheNotifications();
                        this.hasMore = result.Data.HasMore;
                    } else {
                        this.fetchError =
                            "There was an error fetching more notifications. If the problem " +
                            "persists, please refresh the page and try again.";
                    }
                } finally {
                    this.fetchingDfd = false;
                }
            },
            onNotificationsScroll($event) {
                if (isNearBottom($event.currentTarget)) {
                    this.fetchNotifications();
                }
            },
            async fetchOrRetrieveNotificationsForStartup() {
                const lastFetchedTimestamp = localStorage.getItem(this.lastFetchedTimestampKey) || 0;
                const now = Date.now();

                // Fetch notifications if we don't have a "lastFetched" time in localStorage
                // or if that time was more than autoFetchDelay minutes ago. The stored
                // timestamp and now timestamp are both in ms, whereas the delay is in minutes.
                if (now - lastFetchedTimestamp > autoFetchDelayMins * 60 * 1000) {
                    await this.fetchNotifications(true);
                } else {
                    const cachedNotifications = this.retrieveCachedNotifications();
                    this.notifications = cachedNotifications;
                }

                // Inform navbar to display notifications menu item
                if (
                    this.notifications.length > 0 &&
                    this.notifications.some((n) => {
                        return !n.IsHidden;
                    })
                ) {
                    this.$emit("has-notifications");

                    // Inform navbar to display new-notification badge
                    if (
                        this.notifications.some((n) => {
                            return !n.IsHidden && this.isNotificationNew(n);
                        })
                    ) {
                        this.$emit("new-notifications");
                    }
                }
            },
            retrieveCachedNotifications() {
                const notificationsStr = localStorage.getItem(this.notificationsCacheKey);
                if (notificationsStr) {
                    return JSON.parse(notificationsStr);
                }
                return [];
            },
            cacheNotifications() {
                const serializedNotifications = JSON.stringify(this.notifications);
                safeSetItem(this.notificationsCacheKey, serializedNotifications);
            },
            updateNotificationsSeenTime() {
                if (this.notifications.length == 0) {
                    return;
                }

                const notificationsSeenTime = this.notifications[0].PublicationTimeUtcTicks;
                this.$serviceControllers.NotificationController.UpdateNotificationsSeenTimestamp({
                    programId: this.$app.ProgramId,
                    timestampTicks: notificationsSeenTime
                });

                this.lastViewedNotificationTimestampTicks = notificationsSeenTime;
            },
            isNotificationNew(n) {
                return this.lastViewedNotificationTimestampTicks < n.PublicationTimeUtcTicks;
            }
        }
    };
</script>
